import React, { useState } from 'react';
import {
  Box, Typography, Avatar, Divider, IconButton, Button,
  List, ListItem, Chip, Stack, useTheme, alpha, TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Cake as CakeIcon,
  Event as EventIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { Membre, Relation } from '../types';

interface Props {
  membre: Membre | null;
  membres: Membre[];
  relations: Relation[];
  onClose: () => void;
  onEdit: (membre: Membre) => void;
  onDelete: (id: number) => void;
  onAddRelation: (sourceId: number) => void;
  onDeleteRelation: (id: number) => void;
  onUpdateRelation: (id: number, data: { date_debut?: string; date_fin?: string }) => void;
  onSelectMember: (id: number) => void;
}

const RELATION_LABELS: Record<string, { label: string; emoji: string }> = {
  parent: { label: 'Parent de', emoji: '👨‍👩‍👧' },
  grand_parent: { label: 'Grand-parent de', emoji: '👴' },
  arriere_grand_parent: { label: 'Arrière-grand-parent de', emoji: '🏛️' },
  conjoint: { label: 'Conjoint(e) de', emoji: '💑' },
  ex_conjoint: { label: 'Ex-conjoint(e) de', emoji: '💔' },
  frere: { label: 'Frère de', emoji: '👬' },
  soeur: { label: 'Sœur de', emoji: '👭' },
  demi_frere: { label: 'Demi-frère de', emoji: '👦' },
  demi_soeur: { label: 'Demi-sœur de', emoji: '👧' },
};

const RELATION_LABELS_INVERSE: Record<string, { label: string; emoji: string }> = {
  parent: { label: 'Enfant de', emoji: '👶' },
  grand_parent: { label: 'Petit-enfant de', emoji: '🧒' },
  arriere_grand_parent: { label: 'Arrière-petit-enfant de', emoji: '🧒' },
  conjoint: { label: 'Conjoint(e) de', emoji: '💑' },
  ex_conjoint: { label: 'Ex-conjoint(e) de', emoji: '💔' },
  frere: { label: 'Frère de', emoji: '👬' },
  soeur: { label: 'Sœur de', emoji: '👭' },
  demi_frere: { label: 'Demi-frère de', emoji: '👦' },
  demi_soeur: { label: 'Demi-sœur de', emoji: '👧' },
};

const genderColor = (sexe?: string) => {
  if (sexe === 'Homme') return '#3B82F6';
  if (sexe === 'Femme') return '#EC4899';
  return '#6B7280';
};

const formatDate = (date?: string) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const calcAge = (naissance?: string, deces?: string): string | null => {
  if (!naissance) return null;
  const birth = new Date(naissance);
  const end = deces ? new Date(deces) : new Date();
  const age = Math.floor((end.getTime() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  return deces ? `${age} ans (décédé)` : `${age} ans`;
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={700}
    color="text.disabled"
    sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 1 }}
  >
    {children}
  </Typography>
);

const MemberDrawer: React.FC<Props> = ({
  membre, membres, relations, onClose, onEdit, onDelete,
  onAddRelation, onDeleteRelation, onUpdateRelation, onSelectMember,
}) => {
  const theme = useTheme();

  // Édition inline des dates de mariage / divorce d'une relation conjugale
  const [editingRelId, setEditingRelId] = useState<number | null>(null);
  const [editDebut, setEditDebut] = useState('');
  const [editFin, setEditFin] = useState('');

  const startEditDates = (rel: Relation) => {
    setEditingRelId(rel.id);
    setEditDebut(rel.date_debut ? rel.date_debut.slice(0, 10) : '');
    setEditFin(rel.date_fin ? rel.date_fin.slice(0, 10) : '');
  };

  const saveEditDates = (rel: Relation) => {
    onUpdateRelation(rel.id, {
      date_debut: editDebut || undefined,
      date_fin: rel.type_relation === 'ex_conjoint' && editFin ? editFin : undefined,
    });
    setEditingRelId(null);
  };

  if (!membre) return null;

  const color = genderColor(membre.sexe);
  const initials = `${membre.prenom?.[0] || ''}${membre.nom?.[0] || ''}`.toUpperCase();
  const age = calcAge(membre.date_naissance, membre.date_deces);
  const memberRelations = relations.filter(
    r => r.membre_source === membre.id || r.membre_cible === membre.id
  );

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
        {/* Hero header */}
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(135deg, ${alpha(color, 0.14)}, ${alpha(color, 0.04)})`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={membre.photo || undefined}
              sx={{
                width: 60,
                height: 60,
                bgcolor: alpha(color, 0.18),
                color,
                fontWeight: 800,
                fontSize: '1.3rem',
                border: `2px solid ${alpha(color, 0.35)}`,
              }}
            >
              {!membre.photo && initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} noWrap>
                {membre.prenom} {membre.nom}
              </Typography>
              {membre.nom_jeune_fille && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                  née {membre.nom_jeune_fille}
                </Typography>
              )}
              {membre.prenom_naissance && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                  {membre.prenom_naissance_accord || 'né·e'} {membre.prenom_naissance}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                {membre.sexe && (
                  <Chip
                    label={membre.sexe}
                    size="small"
                    sx={{
                      bgcolor: alpha(color, 0.15),
                      color,
                      fontWeight: 700,
                      height: 18,
                      fontSize: '0.68rem',
                    }}
                  />
                )}
                {membre.statut_marital && (
                  <Chip
                    label={membre.statut_marital}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.68rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {/* Dates */}
          {(membre.date_naissance || membre.date_deces) && (
            <Box sx={{ mb: 2.5 }}>
              <SectionLabel>Dates</SectionLabel>
              <Stack spacing={0.75}>
                {membre.date_naissance && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <CakeIcon fontSize="small" sx={{ color: 'text.disabled', fontSize: 16, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2">
                        Naissance : <strong>{formatDate(membre.date_naissance)}</strong>
                      </Typography>
                      {membre.lieu_naissance && (
                        <Typography variant="caption" color="text.secondary">📍 {membre.lieu_naissance}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {membre.date_deces && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pl: 0.25 }}>
                    <Box sx={{ width: 16, display: 'flex', justifyContent: 'center', mt: 0.3 }}>
                      <Box sx={{ width: 2, height: 16, bgcolor: 'text.disabled', opacity: 0.3 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2">
                        Décès : <strong>{formatDate(membre.date_deces)}</strong>
                      </Typography>
                      {membre.lieu_deces && (
                        <Typography variant="caption" color="text.secondary">📍 {membre.lieu_deces}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {age && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
                    {age}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {/* Notes */}
          {membre.notes && (
            <Box sx={{ mb: 2.5 }}>
              <SectionLabel>Notes</SectionLabel>
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  borderRadius: 2,
                  p: 1.5,
                  borderLeft: `3px solid ${alpha(color, 0.4)}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {membre.notes}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Relations */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <SectionLabel>Relations ({memberRelations.length})</SectionLabel>
              <Button
                size="small"
                startIcon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => onAddRelation(membre.id)}
                sx={{ fontSize: '0.72rem', py: 0.25, px: 1, minHeight: 0 }}
              >
                Lier
              </Button>
            </Box>

            {memberRelations.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', fontSize: '0.82rem' }}>
                Aucune relation définie
              </Typography>
            ) : (
              <List disablePadding dense>
                {memberRelations.map(rel => {
                  const isSource = rel.membre_source === membre.id;
                  const otherId = isSource ? rel.membre_cible : rel.membre_source;
                  const other = membres.find(m => m.id === otherId);
                  if (!other) return null;
                  const labelMap = isSource ? RELATION_LABELS : RELATION_LABELS_INVERSE;
                  let relInfo = labelMap[rel.type_relation] || {
                    label: rel.type_relation,
                    emoji: '🔗',
                  };
                  // Conjoint : avec date de mariage => marié(e), sinon => en couple
                  if (rel.type_relation === 'conjoint') {
                    relInfo = rel.date_debut
                      ? { label: 'Marié(e) à', emoji: '💍' }
                      : { label: 'En couple avec', emoji: '❤️' };
                  }
                  const otherColor = genderColor(other.sexe);
                  const isConjugal = rel.type_relation === 'conjoint' || rel.type_relation === 'ex_conjoint';

                  if (editingRelId === rel.id) {
                    return (
                      <ListItem key={rel.id} disablePadding sx={{ mb: 0.5 }}>
                        <Box sx={{ width: '100%', p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                            Dates avec {other.prenom}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <TextField
                              label="Mariage" type="date" size="small" fullWidth
                              value={editDebut} onChange={e => setEditDebut(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                            {rel.type_relation === 'ex_conjoint' && (
                              <TextField
                                label="Divorce" type="date" size="small" fullWidth
                                value={editFin} onChange={e => setEditFin(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1.25}>
                            <Button size="small" onClick={() => setEditingRelId(null)}>Annuler</Button>
                            <Button size="small" variant="contained" disableElevation startIcon={<CheckIcon sx={{ fontSize: 16 }} />} onClick={() => saveEditDates(rel)}>
                              Enregistrer
                            </Button>
                          </Stack>
                        </Box>
                      </ListItem>
                    );
                  }

                  return (
                    <ListItem
                      key={rel.id}
                      disablePadding
                      sx={{ mb: 0.5 }}
                      secondaryAction={
                        <Box sx={{ display: 'flex' }}>
                          {isConjugal && (
                            <IconButton
                              size="small"
                              onClick={() => startEditDates(rel)}
                              title="Dates de mariage / divorce"
                              sx={{
                                color: 'text.disabled',
                                '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) },
                              }}
                            >
                              <EventIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => onDeleteRelation(rel.id)}
                            sx={{
                              color: 'text.disabled',
                              '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      }
                    >
                      <Box
                        onClick={() => onSelectMember(other.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flex: 1,
                          borderRadius: 2,
                          px: 1,
                          py: 0.75,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha(otherColor, 0.15),
                            color: otherColor,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                          }}
                        >
                          {`${other.prenom?.[0] || ''}${other.nom?.[0] || ''}`.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} fontSize="0.82rem">
                            {other.prenom} {other.nom}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {relInfo.emoji} {relInfo.label}
                          </Typography>
                          {(rel.date_debut || rel.date_fin) && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                              {rel.date_debut && `💍 ${formatDate(rel.date_debut)}`}
                              {rel.date_debut && rel.date_fin && ' · '}
                              {rel.date_fin && `💔 ${formatDate(rel.date_fin)}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>

        {/* Footer actions */}
        <Divider />
        <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => onEdit(membre)}
            size="small"
            fullWidth
          >
            Modifier
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={() => {
              if (window.confirm(`Supprimer ${membre.prenom} ${membre.nom} et toutes ses relations ?`))
                onDelete(membre.id);
            }}
            size="small"
            sx={{ minWidth: 0, px: 1.5 }}
          >
            Sup.
          </Button>
        </Box>
    </Box>
  );
};

export default MemberDrawer;
